/**
 * Unified client initialization for workers and frontends
 * Consolidates scattered initialization patterns to prevent configuration bugs
 */

import { newWorkersChannelClient, useSandboxWrappers } from 'workers-channel-js'
import { newNamespaceClient as createNamespaceClient } from 'workers-namespace-js'
import { defaultSandbox } from './defaults.js'
import { wrappers } from './index.js'

// Service URL constants
export const CHANNEL_SERVICE_URL = 'https://channel.monoqueue.com'
export const CHANNEL_WEBSOCKET_URL = 'wss://channel.monoqueue.com/ws'
export const NAMESPACE_SERVICE_URL = 'https://namespace.monoqueue.com'

type Sandbox = Record<string, unknown>
type SandboxFn = (...args: unknown[]) => unknown | Promise<unknown>
type Wrapper = (name: string, fn: SandboxFn) => SandboxFn
type WrappersFactory = (deps: { namespaceClient: unknown }) => Wrapper[]

export interface CreateWorkerClientArgs {
  privateKey: string
  sandbox: Sandbox
  middlewares?: Wrapper[] | WrappersFactory
  permissions?: unknown[]
  callTimeout?: number
  enableNamespace?: boolean
  callTransport?: 'ws' | 'http' | 'auto'
  serviceUrl?: string
  websocketUrl?: string
  namespaceUrl?: string
  forceConnect?: boolean
  onDuplicateRejected?: (e: unknown) => void
  onDisplaced?: (e: unknown) => void
}

export const createWorkerClient = ({
  privateKey,
  sandbox,
  middlewares,
  permissions,
  callTimeout = 30000,
  enableNamespace = true,
  callTransport = 'ws',
  serviceUrl = CHANNEL_SERVICE_URL,
  websocketUrl = CHANNEL_WEBSOCKET_URL,
  namespaceUrl = NAMESPACE_SERVICE_URL,
  forceConnect = process.env.FORCE_CONNECT === 'true',
  onDuplicateRejected,
  onDisplaced,
}: CreateWorkerClientArgs): unknown => {
  if (!privateKey) throw new Error('[createWorkerClient] privateKey is required')
  if (!sandbox) throw new Error('[createWorkerClient] sandbox is required')

  // Create namespace client if enabled
  let namespaceClient: unknown = null
  if (enableNamespace) {
    namespaceClient = createNamespaceClient({
      namespaceUrl,
      selector: 'random',
    })
  }

  // Merge default methods (worker's own methods take priority)
  let finalSandbox: any = { ...defaultSandbox, ...sandbox }

  // Apply middleware: custom array/function or auto-generated from permissions
  if (middlewares) {
    const resolved =
      typeof middlewares === 'function' ? middlewares({ namespaceClient }) : middlewares
    finalSandbox = useSandboxWrappers(finalSandbox, resolved as any)
  } else if (permissions) {
    finalSandbox = useSandboxWrappers(finalSandbox, [
      wrappers.requestLog as any,
      wrappers.SimplePermissionControl(permissions as any, {
        getNamespaceClient: () => namespaceClient as any,
      }) as any,
    ])
  }

  const client = newWorkersChannelClient({
    serviceUrl,
    websocketUrl,
    privateKey,
    sandbox: finalSandbox,
    callTransport,
    callTimeout,
    namespaceClient: namespaceClient as any,
    forceConnect,
    onDuplicateRejected,
    onDisplaced,
  })

  return client
}

export interface CreateFrontendClientArgs {
  getPrivateKey: () => string | null | undefined
  getAccessToken: () => Promise<string | null | undefined>
  sandbox?: Sandbox
  serviceUrl?: string
  namespaceUrl?: string
  callTimeout?: number
}

export const createFrontendClient = ({
  getPrivateKey,
  getAccessToken,
  sandbox,
  serviceUrl = CHANNEL_SERVICE_URL,
  namespaceUrl = NAMESPACE_SERVICE_URL,
  callTimeout,
}: CreateFrontendClientArgs): any => {
  if (!getPrivateKey) throw new Error('[createFrontendClient] getPrivateKey is required')
  if (!getAccessToken) throw new Error('[createFrontendClient] getAccessToken is required')

  const privateKey = getPrivateKey()
  if (!privateKey) throw new Error('[createFrontendClient] getPrivateKey() returned null/undefined')

  const namespaceClient = createNamespaceClient({
    namespaceUrl,
    selector: 'random',
  })

  const clientConfig: any = {
    serviceUrl,
    privateKey,
    callTransport: 'http',
    namespaceClient,
    ...(sandbox && { sandbox }),
  }

  if (callTimeout) {
    clientConfig.callTimeout = callTimeout
  }

  const client: any = newWorkersChannelClient(clientConfig)

  client.callWithAuth = async (
    worker: string,
    method: string,
    params: unknown[],
    options: any = {},
  ) => {
    const accessToken = await getAccessToken()
    if (accessToken) {
      options.headers = { ...options.headers, authorization: `Bearer ${accessToken}` }
    }
    return client.call(worker, method, params, options)
  }

  if (client.callByName) {
    client.callByNameWithAuth = async (
      workerName: string,
      method: string,
      params: unknown[],
      options: any = {},
    ) => {
      const accessToken = await getAccessToken()
      if (accessToken) {
        options.headers = { ...options.headers, authorization: `Bearer ${accessToken}` }
      }
      return client.callByName(workerName, method, params, options)
    }
  }

  return client
}

/**
 * Generate ephemeral Ed25519 private key
 */
export const generateEphemeralPrivateKey = (): string => {
  const crypto = globalThis.crypto || (typeof window !== 'undefined' && (window as any).crypto)
  if (!crypto?.getRandomValues) {
    throw new Error('[generateEphemeralPrivateKey] Web Crypto API not available')
  }

  const bytes = crypto.getRandomValues(new Uint8Array(32))
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}
